import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import config from "../../config/config";
import styles from "./Sponsors.module.css";
import { SponsorService } from "../../services/sponsor_service";
import { SponsorAdvertisementService } from "../../services/sponsor_advertisement_service";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaArrowRight,
  FaHandshake,
} from "react-icons/fa";
import { useSponsorsState } from "../../states/useSponsorsState";

const Sponsors = () => {
  const sponsorService = SponsorService();
  const sponsorAdvertisementService = SponsorAdvertisementService();
  const { sponsors, setSponsors, filteredSponsors, setFilteredSponsors, loading, setLoading, searchTerm, setSearchTerm, activeFilter, setActiveFilter, adCounts, setAdCounts } = useSponsorsState();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSponsors();
  }, []);

  useEffect(() => {
    filterSponsors();
  }, [sponsors, searchTerm, activeFilter, adCounts]);

  const fetchSponsors = async () => {
    try {
      await sponsorService.fetchAllSponsor(async (sponsorsData) => {
        // Fetch all advertisements to count per sponsor
        await sponsorAdvertisementService.fetchAllSponsorAdvertisement((ads) => {
          // Count advertisements for each sponsor
          const counts = {};
          ads.forEach(ad => {
            if (ad.sponsor) {
              const sponsorId = ad.sponsor.toString();
              counts[sponsorId] = (counts[sponsorId] || 0) + 1;
            }
          });

          setAdCounts(counts);

          // Sort sponsors by number of advertisements (descending)
          const sortedSponsors = sponsorsData.sort((a, b) => {
            const countA = counts[a.id] || 0;
            const countB = counts[b.id] || 0;

            // First sort by ad count (higher first)
            if (countB !== countA) {
              return countB - countA;
            }

            // If same ad count, sort by active status (active first)
            if (a.is_active !== b.is_active) {
              return b.is_active - a.is_active;
            }

            // If same status, sort by creation date (newer first)
            return new Date(b.created_at) - new Date(a.created_at);
          });

          setSponsors(sortedSponsors);
        });
      });
    } catch (error) {
      console.error("Error fetching sponsors", error);
    } finally {
      setLoading(false);
    }
  };

  const filterSponsors = () => {
    let filtered = sponsors;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(sponsor =>
        sponsor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sponsor.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sponsor.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by active status
    if (activeFilter === "active") {
      filtered = filtered.filter(sponsor => sponsor.is_active);
    } else if (activeFilter === "inactive") {
      filtered = filtered.filter(sponsor => !sponsor.is_active);
    }

    setFilteredSponsors(filtered);
  };

  const getFullImageUrl = (iconPath) => {
    if (!iconPath) return "/default-sponsor.png";
    if (iconPath.startsWith("http")) return iconPath;
    return `${config.baseURL}${iconPath}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getAdCount = (sponsorId) => {
    return adCounts[sponsorId] || 0;
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text, length = 120) => {
    if (!text) return "";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Sponsors...</p>
      </div>
    );
  }

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const staggerChildren = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className={styles.sponsorsPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOverlay}></div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.container}>
            <div className={styles.heroBadge}>Our Partners</div>
            <h1 className={styles.heroTitle}>
              <span>Official Sponsors</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Meet the incredible partners who make the Royal Premier League possible
            </p>
          </div>
        </div>
      </section>

      {/* Sponsors Grid Section */}
      <section className={`${styles.sponsorsSection} ${styles.section}`}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Our Sponsors</h2>
          </div>

          {filteredSponsors.length === 0 ? (
            <div className={styles.noSponsors}>
              <div className={styles.noSponsorsIcon}>  <FaHandshake /></div>
              <h3>No Sponsors Found</h3>
              <p>
                {searchTerm
                  ? "No sponsors match your search criteria"
                  : "No sponsors available at the moment"}
              </p>
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <motion.div
              className={styles.sponsorsGrid}
              variants={staggerChildren}
              initial="hidden"
              animate="visible"
            >
              {filteredSponsors.map((sponsor, index) => (
                <motion.div
                  key={sponsor.id}
                  className={`${styles.sponsorCard} ${getAdCount(sponsor.id) > 0 ? styles.hasAds : ""
                    } ${index === 0 && getAdCount(sponsor.id) > 0 ? styles.topSponsor : ""
                    }`}
                  variants={fadeUp}
                  style={{ "--card-index": index }}
                >
                  <Link to={`/sponsor/${sponsor.id}`} style={{ textDecoration: "none" }}>
                    <div className={styles.sponsorHeader}>
                      <div className={styles.logoContainer}>
                        {sponsor.logo ? (
                          <img
                            src={getFullImageUrl(sponsor.logo)}
                            alt={sponsor.name}
                            className={styles.sponsorLogo}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                      </div>
                      <div className={styles.sponsorStatus}>
                        <span
                          className={`${styles.statusBadge} ${sponsor.is_active ? styles.active : styles.inactive
                            }`}
                        >
                          {sponsor.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <div className={styles.sponsorContent}>
                      <h3 className={styles.sponsorName}>{sponsor.name}</h3>
                      {sponsor.description && (
                        <div className={styles.sponsorDescription}>
                          {truncateText(stripHtml(sponsor.description), 120)}
                        </div>
                      )}

                      <div className={styles.contactInfo}>
                        {sponsor.representative_name && (
                          <div className={styles.contactItem}>
                            <FaUser />
                            <span>{sponsor.representative_name}</span>
                          </div>
                        )}
                        {sponsor.email && (
                          <div className={styles.contactItem}>
                            <FaEnvelope />
                            <span>{sponsor.email}</span>
                          </div>
                        )}
                        {sponsor.phone && (
                          <div className={styles.contactItem}>
                            <FaPhone />
                            <span>{sponsor.phone}</span>
                          </div>
                        )}
                      </div>

                      {sponsor.city && (
                        <div className={styles.location}>
                          <FaMapMarkerAlt />
                          <span>
                            {sponsor.city}
                            {sponsor.country && `, ${sponsor.country}`}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.sponsorFooter}>
                      <div className={styles.joinDate}>
                        Joined {formatDate(sponsor.created_at)}
                      </div>

                      <p className={styles.viewDetails}>
                        View Details <FaArrowRight />
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Sponsors;